// ProjectConfigModal.jsx - Project configuration modal content
import React, { Suspense, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Settings, Upload, X, Loader2, Trash2, ClipboardPaste, Image as ImageIcon, Copy, Check, Share2 } from 'lucide-react';
import ClipboardPasteModal from '../../components/ClipboardPasteModal';
import { API } from '@/App';

const MapLocationPicker = React.lazy(() => import('../../components/MapLocationPicker'));

const IMAGE_SIZE = 400;
const TOOLBAR_H = 40;
const MAP_HEIGHT = IMAGE_SIZE + TOOLBAR_H;

// Loading indicator component
const LoadingIndicator = ({ message, progressPercent }) => (
  <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-50">
    <div className="text-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-2" />
      <p className="text-sm text-gray-600">{message}</p>
      {progressPercent > 0 && (
        <div className="w-48 h-2 bg-gray-200 rounded-full mt-2">
          <div 
            className="h-full bg-orange-500 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </div>
  </div>
);

// Debounced Note Textarea for item notes
const DebouncedNoteTextarea = ({ value, onChange, tempId, placeholder, maxLength = 50 }) => {
  const [localValue, setLocalValue] = React.useState(value || '');
  const timeoutRef = React.useRef(null);

  React.useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onChange(tempId, newValue);
    }, 300);
  };

  return (
    <input
      type="text"
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      maxLength={maxLength}
      className="mt-1 w-full text-xs border border-gray-200 rounded px-2 py-1 focus:border-orange-300 focus:ring-1 focus:ring-orange-300"
    />
  );
};

// Location header: label left, coords + copy right
const LocationHeader = ({ latitude, longitude }) => {
  const [copied, setCopied] = React.useState(false);
  const hasCoords = latitude != null && longitude != null && !isNaN(latitude) && !isNaN(longitude);

  const handleCopy = () => {
    if (!hasCoords) return;
    navigator.clipboard.writeText(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex items-center justify-between mb-2">
      <label className="text-sm font-medium text-gray-700">Location</label>
      {hasCoords && (
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span>Lat: <span className="font-mono text-slate-700">{latitude.toFixed(6)}</span></span>
          <span>Lng: <span className="font-mono text-slate-700">{longitude.toFixed(6)}</span></span>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-orange-600 transition-colors duration-150"
            title="Copy coordinates"
            data-testid="map-copy-coords-btn"
          >
            {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
            {copied && <span className="text-green-500 font-medium">Copied</span>}
          </button>
        </div>
      )}
    </div>
  );
};


export const ProjectConfigModal = ({
  // Modal state
  open,
  onOpenChange,
  hasUnsavedChanges,
  onShowUnsavedModal,
  onClose,
  
  // Tab state
  configTab,
  onTabChange,
  
  // Saving state
  saving,
  saveProgress,
  saveProgressPercent,
  
  // Project details state
  projectName,
  onProjectNameChange,
  projectAddress,
  onProjectAddressChange,
  projectImagePreview,
  onImageSelect,
  onImageRemove,
  imageInputRef,
  
  // Location state
  projectLatitude,
  projectLongitude,
  onLocationChange,
  onLocationClear,
  
  // Image paste
  onImagePaste,
  
  // Division state
  configDivision,
  onDivisionChange,
  allDivisions,
  
  // Items state
  availableItems,
  loadingAvailableItems,
  selectedItems,
  itemNotes,
  onItemNoteChange,
  onAddItem,
  onRemoveItem,
  
  // Actions
  onSave
}) => {
  const [showPasteModal, setShowPasteModal] = useState(false);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen && hasUnsavedChanges) {
        onShowUnsavedModal();
      } else if (!isOpen) {
        onClose();
      }
    }}>
      <DialogContent className="max-w-6xl h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configure Project
          </DialogTitle>
          <DialogDescription>
            Manage project settings and specifications.
          </DialogDescription>
        </DialogHeader>

        {/* Progress Bar during save */}
        {saving && (
          <LoadingIndicator 
            message={saveProgress || 'Saving...'}
            progressPercent={saveProgressPercent}
          />
        )}

        <Tabs value={configTab} onValueChange={onTabChange} className="flex-1 min-h-0 flex flex-col mt-4">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
            <TabsTrigger value="details">Project Details</TabsTrigger>
            <TabsTrigger value="divisions">Division Specifications</TabsTrigger>
          </TabsList>
          
          {/* Project Details Tab */}
          <TabsContent value="details" className="mt-4 flex-1 min-h-0 flex flex-col data-[state=inactive]:hidden">
            <div className="pr-2 space-y-6 overflow-y-auto flex-1 min-h-0">
              {/* Name/Address */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                  <Input value={projectName} onChange={(e) => onProjectNameChange(e.target.value)} placeholder="Project name" data-testid="project-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Address</label>
                  <Input value={projectAddress} onChange={(e) => onProjectAddressChange(e.target.value)} placeholder="Project address" data-testid="project-address-input" />
                </div>
              </div>
              
              {/* Project Image & Location — side by side */}
              <div>
                {/* Hidden file input */}
                <input type="file" className="hidden" ref={imageInputRef} accept="image/jpeg,image/png,image/jpg,application/pdf" onChange={onImageSelect} />

                <div className="flex gap-4 items-stretch" data-testid="project-image-location-area">
                  {/* LEFT: Toolbar + Image — stretches to match map height */}
                  <div className="shrink-0 flex flex-col" style={{ width: IMAGE_SIZE }}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Image</label>

                    {/* Toolbar — 50/50 grid above image */}
                    <div
                      className={`grid grid-cols-2 rounded-t-lg border-2 border-b-0 bg-white ${projectImagePreview ? 'border-slate-200' : 'border-dashed border-slate-300'}`}
                      style={{ height: TOOLBAR_H }}
                      data-testid="project-image-toolbar"
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-full rounded-none rounded-tl-lg text-xs hover:bg-orange-50 hover:text-orange-600"
                        onClick={() => setShowPasteModal(true)}
                        data-testid="project-image-paste-btn"
                      >
                        <ClipboardPaste className="h-3.5 w-3.5 mr-1" />
                        Paste
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-full rounded-none rounded-tr-lg text-xs hover:bg-orange-50 hover:text-orange-600"
                        onClick={() => imageInputRef.current?.click()}
                        data-testid="project-image-upload-btn"
                      >
                        <Upload className="h-3.5 w-3.5 mr-1" />
                        Upload
                      </Button>
                    </div>

                    {/* Image container — fixed 400px height */}
                    <div
                      className={`relative overflow-hidden bg-slate-50 rounded-b-lg border-2 border-t-0 ${projectImagePreview ? 'border-slate-200' : 'border-dashed border-slate-300'}`}
                      style={{ height: IMAGE_SIZE }}
                      data-testid="project-image-area"
                    >
                      {projectImagePreview ? (
                        <>
                          <img
                            src={projectImagePreview}
                            alt="Project preview"
                            className="w-full h-full object-contain p-2"
                            data-testid="project-image-preview"
                          />
                          <button
                            type="button"
                            onClick={onImageRemove}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 z-10 transition-colors duration-150"
                            data-testid="project-image-remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
                          <ImageIcon className="h-12 w-12 text-slate-300" />
                          <p className="text-sm text-slate-400">Project Image</p>
                          <p className="text-[10px] text-slate-400">PNG, JPG, PDF (max 5MB)</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* RIGHT: Map — same total height as image+toolbar */}
                  <div className="flex-1 min-w-0 flex flex-col">
                    <LocationHeader latitude={projectLatitude} longitude={projectLongitude} />
                    <Suspense fallback={
                      <div className="rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center flex-1">
                        <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
                      </div>
                    }>
                      <MapLocationPicker
                        latitude={projectLatitude}
                        longitude={projectLongitude}
                        onChange={onLocationChange}
                        onClear={onLocationClear}
                        showCoords={false}
                        className="flex-1"
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Division Specifications Tab */}
          <TabsContent value="divisions" className="mt-2 flex-1 min-h-0 flex flex-col data-[state=inactive]:hidden">
            <div className="flex flex-col pr-2 overflow-y-auto flex-1 min-h-0">
              {/* Division Selector */}
              <div className="flex-shrink-0 mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Division
                </label>
                <select
                  value={configDivision}
                  onChange={(e) => onDivisionChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select a division...</option>
                  {allDivisions.map((div) => (
                    <option key={div.code} value={div.code}>
                      {div.code} - {div.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Two Column Layout */}
              <div className="grid gap-6 flex-1 min-h-0" style={{ gridTemplateColumns: '5fr 7fr' }}>
                {/* Available Items */}
                <div className="flex flex-col min-h-0">
                  <h3 className="text-lg font-semibold mb-3 flex-shrink-0">Available Items</h3>
                  <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 flex-1 overflow-y-auto min-h-0">
                    {loadingAvailableItems ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-gray-500">Loading items...</div>
                      </div>
                    ) : availableItems.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-gray-500 text-center">
                          {configDivision ? 'No items found in this division' : 'Select a division to view items'}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {availableItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg hover:border-orange-300 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-1.5">
                                <span className="font-medium text-gray-900 text-sm truncate">{item.title}</span>
                                <span className="text-xs text-gray-500 flex-shrink-0">({item.code})</span>
                              </div>
                            </div>
                            <button
                              onClick={() => onAddItem(item)}
                              className="ml-2 px-3 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 transition-colors flex-shrink-0"
                            >
                              Add
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Division Items — filtered by active division */}
                {(() => {
                  const divisionItems = configDivision
                    ? selectedItems.filter(item => item.divisionCode === configDivision)
                    : selectedItems;
                  return (
                    <div className="flex flex-col min-h-0">
                      <h3 className="text-lg font-semibold mb-3 flex-shrink-0">Selected Items ({divisionItems.length})</h3>
                      <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 flex-1 overflow-y-auto min-h-0">
                        {divisionItems.length === 0 ? (
                          <div className="flex items-center justify-center h-full">
                            <div className="text-gray-500 text-center">
                              No items selected for this division yet.
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {divisionItems.map((item) => (
                              <div
                                key={item.tempId}
                                className="p-2 bg-white border border-gray-200 rounded-lg"
                              >
                                <div className="flex items-start gap-2">
                                  <button
                                    onClick={() => onRemoveItem(item.tempId)}
                                    className="p-1 rounded text-red-500 hover:bg-red-600 hover:text-white transition-colors"
                                    title="Remove item"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline justify-between gap-1.5">
                                      <div className="flex items-baseline gap-1.5 min-w-0">
                                        <span className="font-medium text-gray-900 text-sm truncate">{item.itemTitle}</span>
                                        <span className="text-xs text-gray-500 flex-shrink-0">({item.itemCode})</span>
                                      </div>
                                      {item.assignedCompanyName && (
                                        <span className="text-[10px] text-blue-600 flex-shrink-0 inline-flex items-center gap-1.5"><Share2 className="h-3 w-3" />{item.assignedCompanyName}</span>
                                      )}
                                    </div>
                                    
                                    {/* Note - editable for all items (new and existing) */}
                                    {item.tempId && (
                                      <DebouncedNoteTextarea
                                        value={itemNotes[item.tempId] || ''}
                                        onChange={onItemNoteChange}
                                        tempId={item.tempId}
                                        placeholder="Add note..."
                                        maxLength={50}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t mt-4 flex-shrink-0">
          <button
            onClick={() => {
              if (hasUnsavedChanges) {
                onShowUnsavedModal();
              } else {
                onClose();
              }
            }}
            disabled={saving}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving || (selectedItems.length === 0 && configTab === 'divisions')}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Clipboard Paste Modal */}
        <ClipboardPasteModal
          isOpen={showPasteModal}
          onClose={() => setShowPasteModal(false)}
          onUpload={(file) => { onImagePaste?.(file); setShowPasteModal(false); }}
          title="Paste Project Image"
          uploading={false}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ProjectConfigModal;
